"use client";

import Image from "next/image";
import { Clock, Home, User, Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import { Button } from "@/components/ui/button";
import { AnimatedSection } from "@/components/ui/animated-section";
import { FAQ } from "@/components/faq/FAQ";
import { toWhatsAppNumber } from "@/lib/phone";

/**
 * Contact routes for the workshop CTAs.
 *
 * WhatsApp, never `tel:` — the owner does not take voice calls, so a phone
 * link would send warm leads to a number nobody answers. The env var is
 * human-authored and may be national or international form, so it goes through
 * `toWhatsAppNumber` rather than a bare digit strip; see `lib/phone.ts`.
 */
const CONTACT_EMAIL = "littlecakesnl@gmail.com";
const WHATSAPP_NUMBER = toWhatsAppNumber(process.env.NEXT_PUBLIC_BAKERY_PHONE);

/**
 * There are no photographs of an actual workshop yet — the first sessions start
 * in September. Rather than ship a bare text page or stage something misleading,
 * these are the owner's own signature cakes, which genuinely demonstrate the
 * shell piping and buttercream work the workshop teaches. `media.galleryNote`
 * says so in as many words. Swap in real session photos once they exist; keep
 * the note honest about what the reader is looking at.
 *
 * Dimensions are the files' true intrinsic sizes, so `next/image` reserves the
 * right box and the page doesn't shift as they load.
 */
const HERO_IMAGE = {
  src: "/images/notext-cake.webp",
  width: 784,
  height: 924,
} as const;

const GALLERY_IMAGES = [
  { src: "/images/signature-cake-card-v2.webp", width: 522, height: 514 },
  { src: "/images/custom-cake-card-v2.webp", width: 560, height: 747 },
  { src: "/images/handwritten-cake.webp", width: 436, height: 390 },
] as const;

type LearnItem = { title: string; description: string };
type PracticalItem = { term: string; description: string };

interface WorkshopsClientProps {
  learnItems: LearnItem[];
  includedItems: string[];
  practicalItems: PracticalItem[];
  /** Alt text per gallery image, index-aligned with `GALLERY_IMAGES`. */
  galleryAlts: string[];
  faqs: Array<{ question: string; answer: string }>;
}

/** Section heading with the brochure's numbered editorial treatment. */
function SectionHeading({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-baseline gap-3 mb-5 md:mb-7">
      <span
        className="text-sm md:text-base font-bold text-primary tabular-nums"
        aria-hidden="true"
      >
        {number}
      </span>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
        {title}
      </h2>
    </div>
  );
}

export function WorkshopsClient({
  learnItems,
  includedItems,
  practicalItems,
  galleryAlts,
  faqs,
}: WorkshopsClientProps) {
  const t = useTranslations("workshops");

  const whatsappHref = WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}`
    : undefined;

  const highlights = [
    { icon: Clock, key: "duration" },
    { icon: Home, key: "location" },
    { icon: User, key: "private" },
  ] as const;

  return (
    <>
      {/* Hero — deliberately NOT wrapped in AnimatedSection.
          That component renders `opacity-0` until hydration runs and the
          IntersectionObserver fires. On above-the-fold content that costs us
          twice: the browser excludes opacity-0 elements from Largest
          Contentful Paint candidates (so the h1 only counts as painted after
          hydration), and if JS fails the headline and both CTAs never become
          visible. Nothing here needs revealing on scroll — it's on screen at
          load. Sections below the fold keep the animation. */}
      <section className="relative">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:gap-12 lg:items-center">
          <div className="text-left max-w-4xl">
            <p className="text-sm md:text-base font-semibold text-primary uppercase tracking-wide mb-2">
              {t("hero.eyebrow")}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-4 leading-tight">
              {t("hero.title")}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-4 md:mb-6">
              {t("hero.subtitle")}
            </p>
            <p className="text-base md:text-lg text-muted-foreground mb-4 max-w-3xl">
              {t("hero.intro")}
            </p>
            <p className="text-base md:text-lg font-semibold mb-6 md:mb-8">
              {t("hero.priceLine")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {whatsappHref && (
                <Button asChild size="lg" className="min-h-[44px]">
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                    {t("hero.ctaPrimary")}
                  </a>
                </Button>
              )}
              <Button asChild size="lg" variant="outline" className="min-h-[44px]">
                <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t("hero.mailSubject"))}`}>
                  {t("hero.ctaSecondary")}
                </a>
              </Button>
            </div>
          </div>
          {/* LCP element on this page: `priority` so it isn't lazy-loaded, and
              real intrinsic dimensions so nothing shifts as it arrives. */}
          <div className="mt-8 lg:mt-0">
            <Image
              src={HERO_IMAGE.src}
              alt={t("media.heroAlt")}
              width={HERO_IMAGE.width}
              height={HERO_IMAGE.height}
              priority
              sizes="(min-width: 1024px) 26rem, 100vw"
              className="w-full h-auto rounded-lg object-cover"
            />
          </div>
        </div>
      </section>

      {/* Highlights. The sr-only h2 keeps these three cards grouped under one
          heading, matching the `h2 → h3` shape of sections 01–05; without it a
          screen-reader heading list shows three stray top-level entries. */}
      <section className="py-8 md:py-12" aria-labelledby="workshop-highlights">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <h2 id="workshop-highlights" className="sr-only">
            {t("highlights.sectionTitle")}
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {highlights.map(({ icon: Icon, key }, index) => (
              <AnimatedSection key={key} animation="fade-up" delay={index * 100}>
                <div className="h-full p-4 md:p-6 border border-gray-200">
                  <Icon className="h-5 w-5 text-primary mb-3" aria-hidden="true" />
                  <h3 className="font-semibold mb-1">{t(`highlights.${key}.title`)}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t(`highlights.${key}.description`)}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* 01 — De basis */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <SectionHeading number="01" title={t("basics.title")} />
            <div className="grid md:grid-cols-2 gap-4">
              {(["optionA", "optionB"] as const).map((option) => (
                <div key={option} className="p-4 md:p-6 border border-gray-200">
                  <span className="inline-block text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                    {t(`basics.${option}.label`)}
                  </span>
                  <h3 className="font-semibold mb-2">{t(`basics.${option}.title`)}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t(`basics.${option}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 02 — Wat je leert */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <SectionHeading number="02" title={t("learn.title")} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {learnItems.map((item, index) => (
                <div key={index} className="p-4 md:p-6 border border-gray-200">
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Style gallery. Unnumbered on purpose — it illustrates section 02
          rather than adding a step, so it stays out of the 01–05 sequence. */}
      <section className="py-8 md:py-12" aria-labelledby="workshop-gallery">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <h2
              id="workshop-gallery"
              className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-3"
            >
              {t("media.galleryTitle")}
            </h2>
            {/* States plainly that these are the baker's own cakes, not photos
                of a workshop. Do not delete this when real session photos land —
                update it. */}
            <p className="text-sm text-muted-foreground max-w-3xl mb-6">
              {t("media.galleryNote")}
            </p>
            <ul className="grid sm:grid-cols-3 gap-4">
              {GALLERY_IMAGES.map((image, index) => (
                <li key={image.src} className="overflow-hidden rounded-lg border border-gray-200">
                  <Image
                    src={image.src}
                    alt={galleryAlts[index] ?? ""}
                    width={image.width}
                    height={image.height}
                    loading="lazy"
                    sizes="(min-width: 640px) 33vw, 100vw"
                    className="w-full h-56 md:h-64 object-cover"
                  />
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* 03 — Inclusief */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <SectionHeading number="03" title={t("included.title")} />
            <ul className="grid sm:grid-cols-2 gap-3 max-w-4xl">
              {includedItems.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check
                    className="h-5 w-5 text-primary shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span className="text-sm md:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* 04 — Praktische informatie.
          Rendered as a definition list rather than the brochure's prose block:
          the deposit and cancellation terms are the commercially important part
          and get lost when they sit mid-paragraph. */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <SectionHeading number="04" title={t("practical.title")} />
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-5 max-w-5xl">
              {practicalItems.map((item, index) => (
                <div key={index}>
                  <dt className="font-semibold mb-1">{item.term}</dt>
                  <dd className="text-sm text-muted-foreground">{item.description}</dd>
                </div>
              ))}
            </dl>
          </AnimatedSection>
        </div>
      </section>

      {/* 05 — Tarieven */}
      <section className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <SectionHeading number="05" title={t("rates.title")} />
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              {(["solo", "duo", "travel"] as const).map((rate) => (
                <div key={rate} className="p-4 md:p-6 border border-gray-200">
                  <h3 className="font-semibold mb-2">{t(`rates.${rate}.title`)}</h3>
                  <p className="text-2xl md:text-3xl font-bold text-primary mb-1">
                    {t(`rates.${rate}.price`)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(`rates.${rate}.description`)}
                  </p>
                </div>
              ))}
            </div>
            <div className="p-4 md:p-6 border border-gray-200 max-w-3xl">
              <span className="inline-block text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                {t("rates.masterclass.tag")}
              </span>
              <h3 className="font-semibold mb-2">{t("rates.masterclass.title")}</h3>
              <p className="text-sm text-muted-foreground mb-2">
                {t("rates.masterclass.description")}
              </p>
              <p className="font-semibold">{t("rates.masterclass.price")}</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      {/* FAQ. The component renders a bare <section> with no container of its
          own, so the caller supplies the page gutters — without this wrapper
          the accordion runs edge-to-edge while every other section is inset.
          Same shape as PricingClient / LocationFAQ; `max-w-3xl` keeps the
          answers at a readable measure. */}
      <div className="py-8 md:py-12">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <AnimatedSection animation="fade-up">
              <FAQ
                faqs={faqs}
                variant="simple"
                title={t("faqTitle")}
                sectionClassName=""
              />
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Closing CTA */}
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fade-up">
            <div className="p-6 md:p-10 border border-gray-200 max-w-4xl">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">{t("cta.title")}</h2>
              <p className="text-muted-foreground mb-6">{t("cta.description")}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                {whatsappHref && (
                  <Button asChild size="lg" className="min-h-[44px]">
                    <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                      {t("cta.whatsapp")}
                    </a>
                  </Button>
                )}
                <Button asChild size="lg" variant="outline" className="min-h-[44px]">
                  <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(t("hero.mailSubject"))}`}>
                    {t("cta.email")}
                  </a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Related pages */}
      <section className="pb-12 md:pb-16">
        <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 md:mb-8">
            {t("relatedTitle")}
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link
              href="/cakes"
              className="p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors"
            >
              <h3 className="font-semibold mb-2">{t("related.cakes.title")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("related.cakes.description")}
              </p>
            </Link>
            <Link
              href="/about/our-baker"
              className="p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors"
            >
              <h3 className="font-semibold mb-2">{t("related.baker.title")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("related.baker.description")}
              </p>
            </Link>
            <Link
              href="/pricing"
              className="p-4 md:p-6 border border-gray-200 hover:border-primary/30 transition-colors"
            >
              <h3 className="font-semibold mb-2">{t("related.pricing.title")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("related.pricing.description")}
              </p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
